import { pantryColors } from '@pantry/ui'
import React, { useMemo, useState } from 'react'
import { useDebounce } from '../../../../hooks/useDebounce'
import type { LoyaltyCouponProductRef } from '../../../../types/loyaltyCoupon'
import { useCouponProductSearch } from '../hooks/useCouponProductSearch'

const p = pantryColors

const inputStyle: React.CSSProperties = {
  boxSizing: 'border-box',
  width: '100%',
  padding: '9px 12px',
  border: `1px solid ${p.gray[200]}`,
  borderRadius: 6,
  fontSize: 13,
  color: p.gray[800],
  background: p.white,
  outline: 'none',
}

const subLabel: React.CSSProperties = {
  fontSize: 11.5,
  fontWeight: 600,
  color: p.gray[500],
  marginBottom: 8,
}

interface Props {
  products: LoyaltyCouponProductRef[]
  onProductsChange: (next: LoyaltyCouponProductRef[]) => void
}

/**
 * "Products" tab body for the coupon modal's Applies-to section.
 *
 * Always renders the explicit-selection picker (search + chips). The legacy
 * "All products" toggle that used to live at the top of this component was
 * removed when the Applies-to section adopted the 3-tab UI — the storewide
 * case is now an explicit "All Products" tab at the parent modal level.
 */
export const CouponModeProducts: React.FC<Props> = ({
  products,
  onProductsChange,
}) => {
  const [query, setQuery] = useState('')
  const debouncedQuery = useDebounce(query, 250)
  // Pass the already-picked product IDs to the hook so the dropdown never
  // surfaces them. Doing it at the hook level (rather than slicing the
  // already-trimmed `results` here) keeps the visible result count stable —
  // the 8-row cap applies *after* exclusion.
  const pickedIds = useMemo(() => products.map((pr) => pr.id), [products])
  const { results, isLoading } = useCouponProductSearch(debouncedQuery, {
    excludeIds: pickedIds,
  })
  const filteredResults = results.slice(0, 8)

  const addProduct = (prod: LoyaltyCouponProductRef) => {
    if (products.some((pr) => pr.id === prod.id)) return
    onProductsChange([...products, prod])
    setQuery('')
  }

  const removeProduct = (id: string) => {
    onProductsChange(products.filter((pr) => pr.id !== id))
  }

  return (
    <div style={{ position: 'relative' }}>
      <div style={subLabel}>Search and pick products</div>
      <input
        style={inputStyle}
        type="text"
        placeholder="Type a product name…"
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        autoComplete="off"
      />
      {query && (
        <div
          style={{
            position: 'absolute',
            top: '100%',
            left: 0,
            right: 0,
            marginTop: 4,
            maxHeight: 220,
            overflowY: 'auto',
            background: p.white,
            border: `1px solid ${p.gray[200]}`,
            borderRadius: 8,
            boxShadow: '0 4px 12px rgba(15, 23, 42, 0.08)',
            zIndex: 2,
          }}
        >
          {isLoading ? (
            <div
              style={{
                padding: '10px 12px',
                fontSize: 12,
                color: p.gray[500],
              }}
            >
              Searching…
            </div>
          ) : filteredResults.length === 0 ? (
            <div
              style={{
                padding: '10px 12px',
                fontSize: 12,
                color: p.gray[500],
              }}
            >
              No products match "{query}"
            </div>
          ) : (
            filteredResults.map((prod) => (
              <button
                key={prod.id}
                type="button"
                onClick={() => addProduct(prod)}
                style={{
                  display: 'block',
                  width: '100%',
                  textAlign: 'left',
                  padding: '8px 12px',
                  fontSize: 13,
                  color: p.gray[800],
                  background: 'transparent',
                  border: 'none',
                  cursor: 'pointer',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = p.gray[50]
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = 'transparent'
                }}
              >
                {prod.name}
              </button>
            ))
          )}
        </div>
      )}

      {products.length > 0 && (
        <div
          style={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: 6,
            marginTop: 10,
          }}
        >
          {products.map((prod) => (
            <span
              key={prod.id}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 4,
                padding: '4px 10px',
                background: '#DCFCE7',
                color: '#15803D',
                borderRadius: 999,
                fontSize: 12,
                fontWeight: 600,
              }}
            >
              {prod.name}
              <button
                type="button"
                onClick={() => removeProduct(prod.id)}
                aria-label={`Remove ${prod.name}`}
                style={{
                  border: 'none',
                  background: 'transparent',
                  color: '#15803D',
                  fontSize: 14,
                  lineHeight: 1,
                  cursor: 'pointer',
                  padding: 0,
                }}
              >
                ×
              </button>
            </span>
          ))}
        </div>
      )}
    </div>
  )
}
